import { isComplementEditedByUser } from 'utils/houseComplement';

export { isComplementEditedByUser };

export const FIXED_HOUSE_ADDRESS_ITEM_KEYS = [
  'complement',
  'floor',
  'number',
  'neighborhood',
  'street',
];

export const FIXED_HOUSE_FORM_ITEM_KEYS = [
  ...FIXED_HOUSE_ADDRESS_ITEM_KEYS,
  'condoName',
  'tipo',
];

const emptyFixedHouseItem = { value: '' };

/** Firestore/API hydrate can omit untouched keys; form components expect every slug. */
export const ensureFixedHouseItemsShape = items => {
  if (!items) {
    return items;
  }

  return FIXED_HOUSE_FORM_ITEM_KEYS.reduce(
    (shaped, key) => ({
      ...shaped,
      [key]: items[key] ?? emptyFixedHouseItem,
    }),
    { ...items },
  );
};

export const hasHouseAddressItemValue = item => {
  const value = item && item.value;

  return value !== undefined && value !== null && value !== '';
};

export const getCachedSessionPropertyAddress = photoSessionDetail => {
  const house = photoSessionDetail && photoSessionDetail.house;

  if (!house) {
    return undefined;
  }

  return {
    neighborhood: house.neighborhood,
    address: house.address,
    number: house.number,
    numberOrBlock: house.numberOrBlock,
    floor: house.floor,
    complement: house.complement,
  };
};

export const buildHouseAddressParamsFromPhotoSession = (
  photoSessionDetail,
  { existingItems, onlyFillMissing = false } = {},
) => {
  const {
    floor,
    complement,
    numberOrBlock,
    address,
    neighborhood,
  } = photoSessionDetail.house;

  const fullParams = {
    floor: {
      value: floor,
    },
    complement: {
      value: complement,
    },
    number: {
      value: numberOrBlock,
    },
    street: {
      value: address,
    },
    neighborhood: {
      value: neighborhood,
    },
  };

  if (!onlyFillMissing) {
    return fullParams;
  }

  return FIXED_HOUSE_ADDRESS_ITEM_KEYS.reduce((params, key) => {
    if (key === 'complement' && isComplementEditedByUser(existingItems)) {
      return params;
    }

    const existing = existingItems && existingItems[key];
    if (
      !hasHouseAddressItemValue(existing)
      && hasHouseAddressItemValue(fullParams[key])
    ) {
      return { ...params, [key]: fullParams[key] };
    }

    return params;
  }, {});
};

export const cloneFixedHouseAddressItems = items => {
  if (!items) {
    return items;
  }

  return Object.keys(items).reduce(
    (clonedItems, key) => ({
      ...clonedItems,
      [key]: { ...items[key] },
    }),
    {},
  );
};

/**
 * When merging address fields from API, Firestore or partial updates,
 * keep an existing non-empty value instead of overwriting with empty.
 */
export const mergeFixedHouseAddressItems = (existingItems, incomingItems) => {
  if (!incomingItems) {
    return existingItems;
  }

  if (!existingItems) {
    return incomingItems;
  }

  const merged = { ...existingItems, ...incomingItems };

  if (isComplementEditedByUser(incomingItems) && incomingItems.complement) {
    merged.complement = incomingItems.complement;
  } else if (isComplementEditedByUser(existingItems) && existingItems.complement) {
    merged.complement = existingItems.complement;
  }

  FIXED_HOUSE_ADDRESS_ITEM_KEYS.forEach(key => {
    const incoming = incomingItems[key];
    const existing = existingItems[key];

    if (key === 'complement' && isComplementEditedByUser(incomingItems)) {
      return;
    }

    if (
      incoming
      && !hasHouseAddressItemValue(incoming)
      && hasHouseAddressItemValue(existing)
    ) {
      merged[key] = existing;
    }
  });

  return merged;
};
